import { useEffect, useRef, useState } from 'react'
import { type Skill } from '../lib/api'
import { Hash, Sparkles, X } from 'lucide-react'
import { clsx } from 'clsx'

/**
 * Slash menu.
 *
 * Shows ALL of Mark's skills (no 100-cap; that's a Telegram thing).
 * Live fuzzy filter as he types after the `/`. Scroll with arrow keys
 * or wheel; Tab picks; Esc closes. Enter follows the composer's device policy.
 */
export default function SlashMenu({
  enterSends,
  query,
  skills,
  isLoading,
  onPick,
  onClose,
  title = 'Skills',
  subtitle,
  emptyText = 'No matches.',
  showCloseButton = false,
}: {
  enterSends: boolean
  query: string
  skills: Skill[]
  isLoading: boolean
  onPick: (skill: Skill) => void
  onClose: () => void
  title?: string
  subtitle?: string
  emptyText?: string
  showCloseButton?: boolean
}) {
  const data = skills
  const [highlighted, setHighlighted] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setHighlighted(0)
    if (listRef.current) listRef.current.scrollTop = 0
  }, [data.length, query])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.defaultPrevented || e.isComposing || e.keyCode === 229) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlighted((h) => Math.min(h + 1, data.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlighted((h) => Math.max(h - 1, 0))
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey && data[highlighted]) {
        e.preventDefault()
        onPick(data[highlighted]!)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [data, highlighted, onPick, onClose])

  // Keep highlighted item in view.
  useEffect(() => {
    const list = listRef.current
    if (!list) return
    const item = list.querySelector<HTMLElement>(`[data-idx="${highlighted}"]`)
    item?.scrollIntoView({ block: 'nearest' })
  }, [highlighted])

  return (
    <div
      role="listbox"
      aria-label="Slash commands"
      className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-2xl shadow-xl z-20 overflow-hidden"
    >
      <div className="px-3 py-2 border-b border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-900/40 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-blue-600" />
        <span className="shrink-0 whitespace-nowrap text-xs font-medium">{title}</span>
        {subtitle ? (
          <span className="min-w-0 truncate text-[11px] text-stone-500">{subtitle}</span>
        ) : query ? (
          <span className="min-w-0 truncate text-[11px] text-stone-500">
            matching <span className="font-mono">{query}</span>
          </span>
        ) : null}
        <span className="ml-auto shrink-0 whitespace-nowrap text-[10px] text-stone-400">
          {data.length} {data.length === 1 ? 'skill' : 'skills'}
          {data.length > 0 && !showCloseButton && ` · ↑↓ navigate · Tab pick · Enter ${enterSends ? 'send' : 'newline'} · Esc close`}
        </span>
        {showCloseButton && (
          <button
            type="button"
            onClick={onClose}
            className="grid h-7 w-7 place-items-center rounded-full text-stone-500 hover:bg-stone-200 dark:hover:bg-stone-700"
            aria-label={`Close ${title.toLowerCase()}`}
            title="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div
        ref={listRef}
        className="max-h-72 overflow-y-auto"
      >
        {isLoading && (
          <div className="px-3 py-3 text-xs text-stone-500">Loading…</div>
        )}
        {!isLoading && data.length === 0 && (
          <div className="px-3 py-3 text-xs text-stone-500">{emptyText}</div>
        )}
        {data.map((s, i) => (
          <button
            key={s.command}
            data-idx={i}
            onClick={() => onPick(s)}
            onMouseEnter={() => setHighlighted(i)}
            className={clsx(
              'w-full text-left px-3 py-2 flex items-start gap-2 transition-colors',
              i === highlighted
                ? 'bg-blue-50 dark:bg-blue-900/30'
                : 'hover:bg-stone-50 dark:hover:bg-stone-700/40',
              !s.enabled_for_lane && 'opacity-40',
            )}
          >
            <Hash className="w-3.5 h-3.5 mt-0.5 text-blue-600 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">
                <span className="font-mono">/{s.name}</span>
              </div>
              {s.description && (
                <div className="text-[11px] text-stone-500 dark:text-stone-400 line-clamp-2">
                  {s.description}
                </div>
              )}
            </div>
            {s.sources && s.sources.length > 0 && (
              <span className="text-[10px] text-stone-400 mt-1 shrink-0">
                {s.sources.join('·')}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
